import * as TooltipPrimitive from '@radix-ui/react-tooltip';
import { Link } from '@tanstack/react-router';
import { HelpCircle } from 'lucide-react';
import React, { createContext, useContext, useMemo } from 'react';
import { type Components } from 'react-markdown';

import { type ReferenceData } from '@apps/web/components/phoebe_chat/assistant_response_types';
import { usePhoebeChatOrganizationScope } from '@apps/web/components/phoebe_chat/chat_organization_scope';
import { ENTITY_CONFIG } from '@apps/web/components/phoebe_chat/entity_visuals';
import { useIsExtensionSurface } from '@apps/web/components/phoebe_chat/extension_surface_context';
import { processSlackUserMentionsForMarkdown } from '@apps/web/components/phoebe_chat/slack_mentions';
import { useEntityDetailClick } from '@apps/web/components/phoebe_chat_view/assistant_responses/entity_detail_click_context';
import { formatTimestamp } from '@apps/web/components/phoebe_chat_view/tool_calls/tool_call_template';
import { Markdown, MarkdownAnchor } from '@apps/web/components/ui/markdown';
import {
    ENTITY_PILL_CLASSES,
    type EntityType,
} from '@apps/web/hooks/use_mention_dropdown';
import { ENTITY_PILL_BASE_CLASSES } from '@apps/web/lib/entity_palette';
import {
    ENTITY_REFERENCE_PATTERN,
    unwrapNestedEntityReferences,
} from '@apps/web/lib/entity_references';
import { cn } from '@apps/web/lib/utils';

export function renderReferenceBadgeLink(
    reference: ReferenceData,
    className: string,
    children: React.ReactNode,
): React.ReactElement | null {
    const onClick = (e: React.MouseEvent) => e.stopPropagation();
    if (reference.type === 'shift') {
        return (
            <Link
                to="/shifts/$shift-id"
                params={{ 'shift-id': reference.id }}
                onClick={onClick}
                className={className}
            >
                {children}
            </Link>
        );
    }
    if (reference.type === 'caregiver') {
        return (
            <Link
                to="/caregivers"
                search={(previous) => ({
                    organizationId:
                        typeof previous.organizationId === 'string'
                            ? previous.organizationId
                            : undefined,
                    mode:
                        previous.mode === 'live' || previous.mode === 'sandbox'
                            ? previous.mode
                            : undefined,
                    caregiverId: reference.id,
                })}
                onClick={onClick}
                className={className}
            >
                {children}
            </Link>
        );
    }
    if (reference.type === 'client') {
        return (
            <Link
                to="/clients"
                search={(previous) => ({
                    organizationId:
                        typeof previous.organizationId === 'string'
                            ? previous.organizationId
                            : undefined,
                    mode:
                        previous.mode === 'live' || previous.mode === 'sandbox'
                            ? previous.mode
                            : undefined,
                    clientId: reference.id,
                })}
                onClick={onClick}
                className={className}
            >
                {children}
            </Link>
        );
    }
    if (reference.type === 'callout') {
        return (
            <Link
                to="/outreaches/$callout-id"
                params={{ 'callout-id': reference.id }}
                onClick={onClick}
                className={className}
            >
                {children}
            </Link>
        );
    }
    return null;
}

function ReferenceHoverCardContent({
    reference,
    timezone,
}: {
    readonly reference: ReferenceData;
    readonly timezone: string;
}) {
    const config =
        reference.type in ENTITY_CONFIG
            ? ENTITY_CONFIG[reference.type as keyof typeof ENTITY_CONFIG]
            : null;
    const Icon = config?.icon ?? HelpCircle;
    const label = config?.label ?? reference.type;
    const color = config?.color ?? 'text-muted-foreground';
    const meta = reference.metadata;

    return (
        <div className="flex flex-col gap-1.5">
            <div className={cn('flex items-center gap-1 font-semibold', color)}>
                <Icon className="size-3.5" />
                <span>{label}</span>
            </div>
            <div className="border-border border-t" />
            <p className="font-medium">{reference.display_name}</p>
            {meta && reference.type === 'shift' && (
                <dl className="text-muted-foreground grid grid-cols-[auto_1fr] gap-x-2 gap-y-0.5">
                    {Boolean(meta['patient_name']) && (
                        <React.Fragment>
                            <dt>Patient</dt>
                            <dd>{String(meta['patient_name'])}</dd>
                        </React.Fragment>
                    )}
                    {Boolean(
                        meta['shift_start_time'] && meta['shift_end_time'],
                    ) && (
                        <React.Fragment>
                            <dt>Time</dt>
                            <dd>
                                {formatTimestamp(
                                    String(meta['shift_start_time']),
                                    timezone,
                                )}
                                {' \u2013 '}
                                {new Date(
                                    String(meta['shift_end_time']),
                                ).toLocaleTimeString(undefined, {
                                    hour: 'numeric',
                                    minute: '2-digit',
                                    timeZone: timezone,
                                })}
                            </dd>
                        </React.Fragment>
                    )}
                    {Boolean(meta['status']) && (
                        <React.Fragment>
                            <dt>Status</dt>
                            <dd>{String(meta['status'])}</dd>
                        </React.Fragment>
                    )}
                </dl>
            )}
            {meta &&
                reference.type === 'caregiver' &&
                Boolean(meta['roles'] || meta['tags']) && (
                    <dl className="text-muted-foreground grid grid-cols-[auto_1fr] gap-x-2 gap-y-0.5">
                        {Boolean(meta['roles']) && (
                            <React.Fragment>
                                <dt>Roles</dt>
                                <dd>
                                    {Array.isArray(meta['roles'])
                                        ? meta['roles']
                                              .map((r) =>
                                                  String(r).toUpperCase(),
                                              )
                                              .join(', ')
                                        : String(meta['roles'])}
                                </dd>
                            </React.Fragment>
                        )}
                        {Boolean(meta['tags']) && (
                            <React.Fragment>
                                <dt>Tags</dt>
                                <dd>
                                    {Array.isArray(meta['tags'])
                                        ? meta['tags'].map(String).join(', ')
                                        : String(meta['tags'])}
                                </dd>
                            </React.Fragment>
                        )}
                    </dl>
                )}
            {meta &&
                reference.type === 'client' &&
                Boolean(meta['city'] || meta['tags']) && (
                    <dl className="text-muted-foreground grid grid-cols-[auto_1fr] gap-x-2 gap-y-0.5">
                        {Boolean(meta['city']) && (
                            <React.Fragment>
                                <dt>City</dt>
                                <dd>{String(meta['city'])}</dd>
                            </React.Fragment>
                        )}
                        {Boolean(meta['tags']) && (
                            <React.Fragment>
                                <dt>Tags</dt>
                                <dd>
                                    {Array.isArray(meta['tags'])
                                        ? meta['tags'].map(String).join(', ')
                                        : String(meta['tags'])}
                                </dd>
                            </React.Fragment>
                        )}
                    </dl>
                )}
        </div>
    );
}

export function ReferenceBadge({
    reference,
}: {
    readonly reference: ReferenceData;
}) {
    const config =
        reference.type in ENTITY_CONFIG
            ? ENTITY_CONFIG[reference.type as keyof typeof ENTITY_CONFIG]
            : null;
    const Icon = config?.icon ?? HelpCircle;
    const { timezone } = usePhoebeChatOrganizationScope();
    const isExtensionSurface = useIsExtensionSurface();
    const { onCaregiverClick, onClientClick, onShiftClick } =
        useEntityDetailClick();

    const pillClassName = cn(
        ENTITY_PILL_BASE_CLASSES,
        'max-w-full min-w-0',
        reference.type in ENTITY_PILL_CLASSES
            ? ENTITY_PILL_CLASSES[reference.type as EntityType]
            : ENTITY_PILL_CLASSES.shift,
    );
    const pillBody = (
        <React.Fragment>
            <Icon className="size-3 shrink-0" />
            <span className="min-w-0 truncate">{reference.display_name}</span>
        </React.Fragment>
    );
    // Chat stays self-contained: caregiver, client, and shift badges open the
    // in-panel detail popup where the reader already is, never navigating out
    // of the page. Types without an in-panel detail (callouts) keep their
    // in-app navigation on the web and render as plain badges in the
    // extension; the same fallback applies if no handler was provided.
    const inPanelDetailClick =
        reference.type === 'caregiver'
            ? onCaregiverClick
            : reference.type === 'client'
              ? onClientClick
              : reference.type === 'shift'
                ? onShiftClick
                : undefined;
    const linkedPill = inPanelDetailClick ? (
        <button
            type="button"
            onClick={(event) => {
                // Badges can sit inside clickable rows (collapsibles, cards);
                // opening the popup should not also trigger the row.
                event.stopPropagation();
                inPanelDetailClick(reference.id);
            }}
            className={cn(pillClassName, 'cursor-pointer')}
        >
            {pillBody}
        </button>
    ) : isExtensionSurface ? null : (
        renderReferenceBadgeLink(
            reference,
            cn(pillClassName, 'cursor-pointer'),
            pillBody,
        )
    );

    return (
        // Self-contained provider so the badge works in any render context —
        // including thinking/thought blocks and tool outputs that aren't nested
        // under the message-level TooltipProvider. Harmless where an ancestor
        // provider already exists (Radix nests providers).
        <TooltipPrimitive.Provider delayDuration={200}>
            <TooltipPrimitive.Root>
                <TooltipPrimitive.Trigger asChild={true}>
                    {linkedPill ?? (
                        <span className={pillClassName}>{pillBody}</span>
                    )}
                </TooltipPrimitive.Trigger>
                <TooltipPrimitive.Portal>
                    <TooltipPrimitive.Content
                        sideOffset={4}
                        className="animate-in fade-in-0 zoom-in-95 border-border bg-popover text-popover-foreground z-50 max-w-64 rounded-md border px-3 py-2 text-sm shadow-md"
                    >
                        <ReferenceHoverCardContent
                            reference={reference}
                            timezone={timezone}
                        />
                    </TooltipPrimitive.Content>
                </TooltipPrimitive.Portal>
            </TooltipPrimitive.Root>
        </TooltipPrimitive.Provider>
    );
}

export function formatReferenceFallbackDisplayName(
    referenceType: string,
): string {
    const normalized = referenceType.trim().replace(/_/g, ' ');
    if (!normalized) {
        return 'Reference';
    }
    return normalized.replace(/^\w/, (c) => c.toUpperCase());
}

function escapeMarkdownLinkText(text: string): string {
    return text.replace(/([\\[\]])/g, '\\$1');
}

function processSlackMrkdwnLinksForMarkdown(content: string): string {
    return content.replace(
        /<((?:https?:\/\/|mailto:)[^>|]+)\|([^>]+)>/g,
        (_match, url: string, label: string) =>
            `[${escapeMarkdownLinkText(label)}](${url})`,
    );
}

const LEADING_DONE_PATTERN = /^\s*Done\s+[—–-]\s*/i;
const SHIFT_REFERENCE_LIST_ITEM_PATTERN =
    /^\s*(?:[-*•]|\d+[.)])\s+\[\[shift:[^\]\n]+]]/;
const SHIFT_REFERENCE_TOKEN_PATTERN = /\[\[shift:[^\]\n]+]]/g;
const SHIFT_COUNT_PREFIX_PATTERN = /^(\s*(?:\*\*)?)(\d+)(\s+.*?\bshifts?\b)/i;
const MONTH_DATE_PATTERN =
    '(?:Jan(?:uary)?|Feb(?:ruary)?|Mar(?:ch)?|Apr(?:il)?|May|' +
    'Jun(?:e)?|Jul(?:y)?|Aug(?:ust)?|Sep(?:t(?:ember)?)?|' +
    'Oct(?:ober)?|Nov(?:ember)?|Dec(?:ember)?)\\s+\\d{1,2}\\b';
const WEEKDAY_DATE_LINE_PATTERN = new RegExp(
    '^(\\s*(?:(?:[-*•]|\\d+[.)])\\s+)?)' +
        '(?:Mon(?:day)?|Tue(?:sday)?|Wed(?:nesday)?|Thu(?:rsday)?|' +
        'Fri(?:day)?|Sat(?:urday)?|Sun(?:day)?),?\\s+' +
        `(${MONTH_DATE_PATTERN})`,
    'i',
);
const MONTH_ABBREVIATIONS = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
] as const;

function escapeRegExp(value: string): string {
    return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function referenceNamePattern(name: string): string {
    // Match the stored name with flexible internal whitespace. EHR-synced
    // names often carry double spaces (empty middle name) or trailing spaces
    // while the agent writes the name single-spaced in prose, so a literal
    // escape would demand the exact irregular spacing and the strip no-ops.
    return name.trim().split(/\s+/).map(escapeRegExp).join('[ \\t]+');
}

function capitalizeFirstAlpha(value: string): string {
    const match = /[A-Za-z]/.exec(value);
    if (!match || match.index === undefined) {
        return value;
    }
    return (
        value.slice(0, match.index) +
        match[0].toUpperCase() +
        value.slice(match.index + 1)
    );
}

function stripLeadingDonePrefix(content: string): string {
    const cleaned = content.replace(LEADING_DONE_PATTERN, '');
    return cleaned === content ? content : capitalizeFirstAlpha(cleaned);
}

function uniqueNonEmptyStrings(
    values: Array<string | null | undefined>,
): Array<string> {
    const seen = new Set<string>();
    const unique: Array<string> = [];
    for (const value of values) {
        const stripped = value?.trim();
        if (!stripped) {
            continue;
        }
        const key = stripped.toLowerCase();
        if (seen.has(key)) {
            continue;
        }
        seen.add(key);
        unique.push(stripped);
    }
    return unique;
}

function stringMetadataValue(
    reference: ReferenceData,
    key: string,
): string | null {
    const value = reference.metadata?.[key];
    return typeof value === 'string' ? value : null;
}

function referenceNameCandidates(reference: ReferenceData): Array<string> {
    // Longest first so a short candidate ("Esther") never strips just the
    // prefix of a longer duplicated name ("Esther Williams"), which would
    // leave a dangling remainder.
    return uniqueNonEmptyStrings([
        stringMetadataValue(reference, 'patient_name'),
        stringMetadataValue(reference, 'caregiver_name'),
        stringMetadataValue(reference, 'client_name'),
        stringMetadataValue(reference, 'name'),
        reference.display_name.split(',', 1)[0],
    ]).sort((a, b) => b.length - a.length);
}

function formatUtcMonthDay(value: string): string | null {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
        return null;
    }
    return `${MONTH_ABBREVIATIONS[date.getUTCMonth()]} ${date.getUTCDate()}`;
}

function referenceDateCandidates(reference: ReferenceData): Array<string> {
    return uniqueNonEmptyStrings([
        reference.display_name.includes(',')
            ? reference.display_name.split(',', 2)[1]
            : null,
        stringMetadataValue(reference, 'shift_start_time')
            ? formatUtcMonthDay(
                  String(stringMetadataValue(reference, 'shift_start_time')),
              )
            : null,
        stringMetadataValue(reference, 'start_time')
            ? formatUtcMonthDay(
                  String(stringMetadataValue(reference, 'start_time')),
              )
            : null,
        stringMetadataValue(reference, 'scheduled_start_time')
            ? formatUtcMonthDay(
                  String(
                      stringMetadataValue(reference, 'scheduled_start_time'),
                  ),
              )
            : null,
    ]);
}

function stripDuplicateReferenceContextFromLine(
    line: string,
    reference: ReferenceData,
): string {
    const token = `[[${reference.type}:${reference.id}]]`;
    const tokenPattern = escapeRegExp(token);
    let cleaned = line;
    for (const name of referenceNameCandidates(reference)) {
        const namePattern = referenceNamePattern(name);
        cleaned = cleaned.replace(
            new RegExp(
                `${tokenPattern}[ \\t]+(?:for|about)[ \\t]+${namePattern}` +
                    '[ \\t]*\\([^\\)\\n]+\\)',
                'gi',
            ),
            token,
        );
        cleaned = cleaned.replace(
            new RegExp(
                `${tokenPattern}[ \\t]+(?:for|about)[ \\t]+${namePattern}` +
                    '(?=[ \\t]*[.,;:]|[ \\t]*$)',
                'gi',
            ),
            token,
        );
        // Possessive form: "[[token]] Esther's reply" -> "[[token]]'s reply".
        // Drop only the name and keep the apostrophe-s so the badge owns the
        // possessive. Runs before the bare-name strip below, whose lookahead
        // deliberately rejects a trailing apostrophe. A name ending in "s" with
        // a bare-apostrophe possessive ("James' shift") is left as-is.
        cleaned = cleaned.replace(
            new RegExp(
                `${tokenPattern}[ \\t]+${namePattern}(?=['’]s(?![\\w'’-]))`,
                'gi',
            ),
            token,
        );
        // The badge already shows the name, so a bare name directly next to
        // its own token (either side) is redundant — common in LLM table
        // cells like "[[caregiver:...]] Esther".
        cleaned = cleaned.replace(
            new RegExp(
                `${tokenPattern}[ \\t]+${namePattern}(?![\\w'’-])`,
                'gi',
            ),
            token,
        );
        cleaned = cleaned.replace(
            new RegExp(
                `(?<![\\w'’-])${namePattern}[ \\t]+(?=${tokenPattern})`,
                'gi',
            ),
            '',
        );
    }

    for (const dateLabel of referenceDateCandidates(reference)) {
        const datePattern = escapeRegExp(dateLabel);
        cleaned = cleaned.replace(
            new RegExp(
                `${tokenPattern}[ \\t]+[—–-][ \\t]+` +
                    `(?:[A-Za-z]{3,9},?[ \\t]+)?${datePattern}` +
                    '(?:,?[ \\t]+[0-9][0-9:apmAPM \\t—–-]*)?',
                'gi',
            ),
            token,
        );
    }

    return cleaned.replace(/[ \t]+([.,;:])/g, '$1').replace(/[ \t]{2,}/g, ' ');
}

function cleanReferenceOutputText(
    content: string,
    referenceMap: ReadonlyMap<string, ReferenceData>,
): string {
    const strippedContent = stripLeadingDonePrefix(content);
    const references = Array.from(referenceMap.values());
    if (references.length === 0) {
        return strippedContent;
    }
    return strippedContent
        .split('\n')
        .map((line) =>
            references.reduce(
                (cleanedLine, reference) =>
                    stripDuplicateReferenceContextFromLine(
                        cleanedLine,
                        reference,
                    ),
                line,
            ),
        )
        .join('\n');
}

function reconcileReferencedShiftCount(content: string): string {
    const lines = content.split('\n');
    const countShiftReferencesInFollowingList = (
        startIndex: number,
    ): number => {
        let shiftItemCount = 0;
        let hasSeenListItem = false;
        for (const line of lines.slice(startIndex)) {
            if (!line.trim()) {
                continue;
            }
            if (SHIFT_REFERENCE_LIST_ITEM_PATTERN.test(line)) {
                hasSeenListItem = true;
                shiftItemCount +=
                    line.match(SHIFT_REFERENCE_TOKEN_PATTERN)?.length ?? 0;
                continue;
            }
            if (hasSeenListItem) {
                break;
            }
            return 0;
        }
        return shiftItemCount;
    };

    const updatedLines = [...lines];
    for (const [index, line] of lines.entries()) {
        const match = SHIFT_COUNT_PREFIX_PATTERN.exec(line);
        if (!match) {
            continue;
        }
        const shiftItemCount = countShiftReferencesInFollowingList(index + 1);
        if (shiftItemCount === 0) {
            continue;
        }
        const currentCount = Number(match[2]);
        if (!Number.isFinite(currentCount) || shiftItemCount <= currentCount) {
            continue;
        }
        let updatedLine =
            `${match[1]}${shiftItemCount}${match[3]}` +
            line.slice(match[0].length);
        if (shiftItemCount !== 1) {
            updatedLine = updatedLine.replace(/\bshift\b/i, 'shifts');
        }
        updatedLines[index] = updatedLine;
        return updatedLines.join('\n');
    }
    return content;
}

function stripUnsourcedWeekdayLabels(content: string): string {
    return content
        .split('\n')
        .map((line) => line.replace(WEEKDAY_DATE_LINE_PATTERN, '$1$2'))
        .join('\n');
}

function prepareReferenceOutputText(
    content: string,
    referenceMap: ReadonlyMap<string, ReferenceData>,
): string {
    return reconcileReferencedShiftCount(
        stripUnsourcedWeekdayLabels(
            cleanReferenceOutputText(content, referenceMap),
        ),
    );
}

function getReferenceById(
    referenceMap: ReadonlyMap<string, ReferenceData>,
    id: string,
): ReferenceData | null {
    const directMatch =
        referenceMap.get(id) ?? referenceMap.get(id.toLowerCase());
    if (directMatch) {
        return directMatch;
    }

    const normalizedId = id.toLowerCase();
    return (
        Array.from(referenceMap.values()).find(
            (reference) => reference.id.toLowerCase() === normalizedId,
        ) ?? null
    );
}

// The path/query/fragment must start with `/ ? #` (or the URL ends at the
// host), so lookalikes like `docs.phoebe.work.evil.example` or
// `docs.phoebe.work@evil.example` are not relabeled as trusted docs links.
const PHOEBE_DOCS_URL_SOURCE =
    'https:\\/\\/docs\\.phoebe\\.work(?:[/?#][^\\s)\\]]*)?';
const EXPLICIT_BARE_DOCS_LINK = new RegExp(
    `\\[(${PHOEBE_DOCS_URL_SOURCE})\\]\\(\\1\\)`,
    'g',
);
const BARE_DOCS_URL_IN_PROSE = new RegExp(
    `(^|\\s)(${PHOEBE_DOCS_URL_SOURCE})`,
    'gm',
);

/**
 * Humanize a bare docs.phoebe.work URL that the agent pasted or that GFM
 * would autolink, so chat shows "Phoebe docs" instead of a raw URL. Runs
 * only in the chat markdown pipeline; a correctly-formatted
 * `[Title](url)` link is left untouched because its URL sits inside
 * `(...)`, never whitespace-led.
 */
export function relabelBareDocsLinks(content: string): string {
    return content
        .replace(EXPLICIT_BARE_DOCS_LINK, '[Phoebe docs]($1)')
        .replace(
            BARE_DOCS_URL_IN_PROSE,
            (_match, prefix: string, url: string) => {
                const trimmed = url.replace(/[.,!?;:]+$/u, '');
                const trailing = url.slice(trimmed.length);
                return `${prefix}[Phoebe docs](${trimmed})${trailing}`;
            },
        );
}

export function processContentForMarkdown(
    content: string,
    referenceMap: ReadonlyMap<string, ReferenceData>,
    slackMentionMap: ReadonlyMap<string, string>,
): string {
    const preparedContent = prepareReferenceOutputText(
        relabelBareDocsLinks(unwrapNestedEntityReferences(content)),
        referenceMap,
    );
    return processSlackUserMentionsForMarkdown(
        processSlackMrkdwnLinksForMarkdown(preparedContent),
        slackMentionMap,
    ).replace(ENTITY_REFERENCE_PATTERN, (_match, type, id) => {
        const normalizedType = type.toLowerCase();
        const reference = getReferenceById(referenceMap, id);
        const displayName =
            reference?.display_name ??
            formatReferenceFallbackDisplayName(normalizedType);
        const referenceId = reference?.id ?? id;
        return (
            `[${escapeMarkdownLinkText(displayName)}]` +
            `(#ref:${normalizedType}:${referenceId})`
        );
    });
}

type ReferenceAnchorContextValue = {
    readonly referenceMap: ReadonlyMap<string, ReferenceData>;
    readonly openInNewTab: boolean;
};

const ReferenceAnchorContext = createContext<ReferenceAnchorContextValue>({
    referenceMap: new Map(),
    openInNewTab: false,
});

function ReferenceAwareAnchor({
    href,
    children,
}: {
    readonly href?: string;
    readonly children?: React.ReactNode;
}) {
    const { referenceMap, openInNewTab } = useContext(ReferenceAnchorContext);
    if (href?.startsWith('#ref:')) {
        const parts = href.slice(5).split(':');
        const refType = parts[0] ?? '';
        const refId = parts.slice(1).join(':');
        const refData = getReferenceById(referenceMap, refId);
        return (
            <ReferenceBadge
                reference={
                    refData ?? {
                        type: refType,
                        id: refId,
                        display_name:
                            formatReferenceFallbackDisplayName(refType),
                    }
                }
            />
        );
    }
    return (
        <MarkdownAnchor href={href} openInNewTab={openInNewTab}>
            {children}
        </MarkdownAnchor>
    );
}

// Pass module-level `components`: inline overrides remount on every render.
export function ReferenceMarkdown({
    children,
    referenceMap,
    openLinksInNewTab = false,
    components,
    className,
}: {
    readonly children: string;
    readonly referenceMap: ReadonlyMap<string, ReferenceData>;
    readonly openLinksInNewTab?: boolean;
    readonly components?: Components;
    readonly className?: string;
}) {
    const contextValue = useMemo(
        () => ({ referenceMap, openInNewTab: openLinksInNewTab }),
        [referenceMap, openLinksInNewTab],
    );
    return (
        <ReferenceAnchorContext.Provider value={contextValue}>
            <Markdown
                className={className}
                components={{ ...components, a: ReferenceAwareAnchor }}
            >
                {children}
            </Markdown>
        </ReferenceAnchorContext.Provider>
    );
}

const UUID_REFERENCE_ID_PATTERN =
    '[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-' +
    '[0-9a-fA-F]{4}-[0-9a-fA-F]{12}';
const LEGACY_AUTOMATION_EVENT_REFERENCE_PATTERN = new RegExp(
    `\\bfor (shift|outreach) (${UUID_REFERENCE_ID_PATTERN})\\b`,
    'g',
);

export function replaceLegacyAutomationEventReferences(
    content: string,
    references: ReadonlyArray<ReferenceData> | undefined,
): string {
    if (!references || references.length === 0) {
        return content;
    }
    const referenceIds = new Set(
        references.map((reference) => reference.id.toLowerCase()),
    );
    return content.replace(
        LEGACY_AUTOMATION_EVENT_REFERENCE_PATTERN,
        (match, entityType: string, entityId: string) => {
            const normalizedId = entityId.toLowerCase();
            if (!referenceIds.has(normalizedId)) {
                return match;
            }
            const referenceType =
                entityType === 'outreach' ? 'callout' : 'shift';
            return `for ${entityType} [[${referenceType}:${normalizedId}]]`;
        },
    );
}
